import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import AppContext from '../../contexts/AppContext';
import { GETTING_STARTED_ITEMS } from './gettingStarted';
import { TASK_TOURS, useOnboardingTour } from './OnboardingTourContext';
import styles from './TaskTourOverlay.module.scss';

/**
 * Spotlight overlay for per-task tours triggered from the Getting-started
 * section on the Help page.
 *
 * Positioning is delegated to antd's Popover (rc-trigger / rc-align under
 * the hood) — it handles auto-flip when the preferred side doesn't fit,
 * viewport clamping, and re-aligns automatically on window scroll/resize.
 * We only supply an invisible fixed-positioned anchor that tracks the
 * highlighted target's rect; the Popover pins itself to that anchor.
 *
 * Target resolution:
 *   The overlay retries `document.querySelector(selector)` for up to
 *   ~2s to give the destination page (e.g. wizard) time to render. If
 *   the target never mounts, the tour auto-ends silently.
 */

const TARGET_RETRY_MS = 100;
const TARGET_RETRY_LIMIT = 20; // 20 × 100ms = 2s max wait
const HIGHLIGHT_PADDING = 8;
const CELEBRATION_MS = 900;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Twelve confetti pieces radiating out from the tooltip's centre. Palette
// is picked to complement the blue tooltip without clashing (soft mix of
// blues, green, gold, and pink). Precomputed to keep the render cheap.
const CONFETTI_PIECES = [
  { color: '#3b82f6', angle: 0 },
  { color: '#22c55e', angle: 30 },
  { color: '#f59e0b', angle: 60 },
  { color: '#ec4899', angle: 90 },
  { color: '#60a5fa', angle: 120 },
  { color: '#a78bfa', angle: 150 },
  { color: '#f43f5e', angle: 180 },
  { color: '#14b8a6', angle: 210 },
  { color: '#eab308', angle: 240 },
  { color: '#38bdf8', angle: 270 },
  { color: '#fb923c', angle: 300 },
  { color: '#84cc16', angle: 330 },
];
const CONFETTI_DISTANCE = 120; // pixels each piece travels from centre

const getElementRect = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

// Map our simple placement names to antd v4's Popover placement values.
// antd supports the same four cardinal names — one-to-one mapping.
type SimplePlacement = 'top' | 'bottom' | 'left' | 'right';

const TaskTourOverlay: React.FC = () => {
  const {
    activeTaskTour,
    taskTourStepIndex,
    nextTaskTourStep,
    endTaskTour,
    markTaskComplete,
    completedTasks,
    chainMode,
    startTaskTour,
  } = useOnboardingTour();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AppContext);
  const isAdmin = React.useMemo(() => {
    if (!user || typeof user !== 'object') return false;
    const u = user as { permissions?: { accessAdmin?: boolean } };
    return Boolean(u.permissions?.accessAdmin);
  }, [user]);
  // Refs that always hold the live values so effects with stale closures
  // (completeOnDisappear, F6 guard) can read them without being in deps.
  const locationRef = useRef(location);
  locationRef.current = location;
  const activeTourRef = useRef(activeTaskTour);
  activeTourRef.current = activeTaskTour;
  const stepIndexRef = useRef(taskTourStepIndex);
  stepIndexRef.current = taskTourStepIndex;

  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  // Separate rect for the click cursor when `step.cursorSelector` is set.
  // Lets the tooltip anchor on one element while the cursor pulses on
  // another (e.g. tooltip on read-only content, cursor on the Next
  // button). Null when the step has no cursorSelector or when its
  // element isn't in the DOM yet.
  const [cursorRect, setCursorRect] = useState<Rect | null>(null);
  // Celebration state: when the merchant hits Got it on the last step,
  // we hold the overlay open for ~900ms while confetti plays, then
  // dismiss. Also stashes the tooltip's centre so the confetti can
  // burst from where the merchant last clicked.
  const [celebration, setCelebration] = useState<{ x: number; y: number } | null>(null);
  const anchorElRef = useRef<HTMLDivElement | null>(null);

  const step = activeTaskTour ? TASK_TOURS[activeTaskTour][taskTourStepIndex] : null;
  const totalSteps = activeTaskTour ? TASK_TOURS[activeTaskTour].length : 0;
  const isLastStep = activeTaskTour ? taskTourStepIndex === totalSteps - 1 : false;

  // Skip steps flagged `skipInChain` when chain mode is active. Applies
  // to the New Campaign / type picker steps on Ad Sales & Offer tours —
  // the first create tour in the chain already teaches that flow, so
  // subsequent create tours jump straight to the wizard. On the very
  // last step of a chain-skipped tour (unlikely but safe), fall through
  // to finish rather than looping.
  useEffect(() => {
    if (!step || !chainMode || !step.skipInChain) return;
    if (isLastStep) {
      endTaskTour();
      return;
    }
    nextTaskTourStep();
  }, [step, chainMode, isLastStep, nextTaskTourStep, endTaskTour]);

  // When a step declares a `route`, navigate there if the current location
  // doesn't already match. This handles cases where the previous step's
  // action navigated to the wrong page (e.g. CatalogTable row click goes
  // to /campaigns/:id instead of /help/tour/campaigns/:id).
  useEffect(() => {
    if (!step?.route) return;
    if (!location.pathname.startsWith(step.route)) {
      history.push(step.route);
    }
    // Re-run only when the step's route changes, not on every location update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.route]);

  // Task-level label above the step title so the merchant always
  // knows which starter task they're in — matters most when chained
  // through all five tasks from the welcome modal.
  const activeTaskLabel = React.useMemo(() => {
    if (!activeTaskTour) return null;
    return GETTING_STARTED_ITEMS.find((it) => it.key === activeTaskTour)?.title ?? null;
  }, [activeTaskTour]);

  // Gate resolution: when a step declares a `gateSelector`, the Next
  // button and step counter stay hidden until an element matching that
  // selector shows up in the document. The moment the gate is
  // satisfied, the tour auto-advances (or finishes on the last step) —
  // the merchant doesn't need to also click Next after completing the
  // real action. Polled at 300ms so slight input debounces still land.
  const [gateSatisfied, setGateSatisfied] = useState(true);
  useEffect(() => {
    if (!step || !step.gateSelector) {
      setGateSatisfied(true);
      return undefined;
    }
    const gateSelector = step.gateSelector;
    setGateSatisfied(!!document.querySelector(gateSelector));
    const interval = window.setInterval(() => {
      setGateSatisfied(!!document.querySelector(gateSelector));
    }, 300);
    return () => window.clearInterval(interval);
  }, [step]);

  // Auto-advance once the gate flips from unsatisfied to satisfied.
  // Small delay so the merchant sees their input "take" before the tour
  // moves on, avoiding a jarring instant transition.
  const prevGateRef = useRef(true);
  useEffect(() => {
    if (!step || !step.gateSelector) {
      prevGateRef.current = true;
      return;
    }
    if (gateSatisfied && !prevGateRef.current) {
      window.setTimeout(() => {
        if (isLastStep) {
          finishTour();
        } else {
          nextTaskTourStep();
        }
      }, 400);
    }
    prevGateRef.current = gateSatisfied;
    // finishTour is stable enough; deps intentionally trimmed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateSatisfied, step, isLastStep]);

  // Fire the "task finished" flow: mark the task complete, fire the
  // confetti burst from the anchor's centre (or screen centre when the
  // anchor is already gone), then end the tour after the animation.
  // Idempotent — a running celebration blocks re-entry so we don't
  // double-fire.
  const celebratingRef = useRef(false);
  const finishTour = (): void => {
    if (celebratingRef.current || !activeTaskTour) return;
    celebratingRef.current = true;
    markTaskComplete(activeTaskTour);
    const el = anchorElRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setCelebration({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    } else {
      // Fallback: burst from the screen centre when the anchor has
      // already unmounted (rare — completeOnDisappear fires while the
      // anchor is still up, but this handles the edge case).
      setCelebration({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    }
    window.setTimeout(() => {
      setCelebration(null);
      celebratingRef.current = false;
      // Chain mode: after celebration, jump into the next uncompleted
      // starter task (filtered by admin access). Navigate using the
      // NEXT tour's first-non-skipped step route so chained creates
      // land where their tour expects, not on the item's default route.
      if (chainMode) {
        const justFinished = activeTaskTour;
        const remaining = GETTING_STARTED_ITEMS.filter((it) => !it.adminOnly || isAdmin).filter(
          (it) => it.key !== justFinished && !completedTasks.has(it.key)
        );
        if (remaining.length > 0) {
          const next = remaining[0];
          const nextTour = TASK_TOURS[next.key];
          // Find first step whose skipInChain flag doesn't apply, then
          // use its route (falls back to item route as a safety net).
          const firstActiveStep = nextTour.find((s) => !s.skipInChain);
          const nextRoute = firstActiveStep?.route ?? next.route;
          history.push(nextRoute);
          startTaskTour(next.key);
          return;
        }
      }
      endTaskTour();
    }, CELEBRATION_MS);
  };

  // Primary CTA behaviour depends on step position:
  //   • Non-final steps → advance to the next step.
  //   • Final step → run the finish flow (celebrate + mark complete + end).
  const handlePrimaryClick = (): void => {
    if (!gateSatisfied && !isLastStep) return;
    if (!isLastStep) {
      nextTaskTourStep();
      return;
    }
    finishTour();
  };

  // Resolve the target element for the current step. Retries briefly to
  // let the destination page mount before giving up. Clears the previous
  // rect immediately on step change so we don't render a stale highlight
  // pointing at the last step's element while searching for the new one.
  useLayoutEffect(() => {
    setTargetRect(null);
    if (!step) return undefined;
    let attempts = 0;
    let cancelled = false;
    const tryFind = (): void => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        setTargetRect(getElementRect(el));
        return;
      }
      attempts += 1;
      if (attempts >= TARGET_RETRY_LIMIT) {
        // Target never appeared — end the tour instead of leaving the
        // merchant with a broken overlay.
        endTaskTour();
        return;
      }
      window.setTimeout(tryFind, TARGET_RETRY_MS);
    };
    tryFind();
    return () => {
      cancelled = true;
    };
  }, [step, endTaskTour]);

  // Re-measure the target on resize/scroll so the spotlight stays glued
  // to the element even when the merchant scrolls the underlying page.
  // antd's rc-align inside Popover also re-aligns on the same events,
  // so the tooltip follows the anchor without any manual intervention.
  useEffect(() => {
    if (!step) return undefined;
    const remeasure = (): void => {
      const el = document.querySelector(step.selector);
      if (el) setTargetRect(getElementRect(el));
      // Also remeasure the optional cursor target so the pulsing cursor
      // stays glued to its own element (e.g. Next button) independently
      // from the tooltip anchor.
      if (step.cursorSelector) {
        const cursorEl = document.querySelector(step.cursorSelector);
        setCursorRect(cursorEl ? getElementRect(cursorEl) : null);
      }
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [step]);

  // Resolve the cursor's separate target (if any) alongside the main
  // target. Retries briefly like the main target lookup so late-mounting
  // elements (e.g. wizard footer that renders after step change) are
  // still caught.
  useLayoutEffect(() => {
    setCursorRect(null);
    if (!step || !step.cursorSelector) return undefined;
    const cursorSelector = step.cursorSelector;
    let attempts = 0;
    let cancelled = false;
    const tryFind = (): void => {
      if (cancelled) return;
      const el = document.querySelector(cursorSelector);
      if (el) {
        setCursorRect(getElementRect(el));
        return;
      }
      attempts += 1;
      if (attempts >= TARGET_RETRY_LIMIT) return; // Give up silently — cursor is decorative.
      window.setTimeout(tryFind, TARGET_RETRY_MS);
    };
    tryFind();
    return () => {
      cancelled = true;
    };
  }, [step]);

  // completeOnDisappear: for steps whose expected action removes the
  // target from the DOM (row click navigating away, wizard unmounting
  // after save-as-draft), poll the target once we've located it. When
  // it stops matching, ADVANCE to the next step — or, if this is the
  // last step, finish the tour with celebration. Only runs after
  // we've seen the target at least once to avoid the initial-mount race.
  useEffect(() => {
    if (!step || !step.completeOnDisappear || !targetRect) return undefined;
    const selector = step.selector;
    // Snapshot the path when the target was found. When the selector
    // disappears we need to tell apart two cases:
    //   A) Merchant performed the expected action (e.g. clicked the row)
    //      → location moved to where the NEXT step expects to be → advance.
    //   B) Merchant navigated elsewhere (sidebar, back) → end cleanly
    //      instead of yanking them to the next step's route.
    // If location hasn't changed at all, it's a pure DOM removal (e.g.
    // a filter hiding the row) → advance normally.
    const entryPath = locationRef.current.pathname;
    const interval = window.setInterval(() => {
      if (!document.querySelector(selector)) {
        window.clearInterval(interval);
        const newPath = locationRef.current.pathname;
        if (newPath !== entryPath) {
          // Location changed — check whether it's the expected destination.
          const tourKey = activeTourRef.current;
          const nextIdx = stepIndexRef.current + 1;
          const nextStep = tourKey ? TASK_TOURS[tourKey]?.[nextIdx] : undefined;
          if (nextStep?.route && newPath.startsWith(nextStep.route)) {
            // Intended navigation to the next step's declared route → advance.
            if (isLastStep) {
              finishTour();
            } else {
              nextTaskTourStep();
            }
          } else {
            // Merchant left the tour context → end without hijacking.
            endTaskTour();
          }
          return;
        }
        if (isLastStep) {
          finishTour();
        } else {
          nextTaskTourStep();
        }
      }
    }, 200);
    return () => window.clearInterval(interval);
    // finishTour / nextTaskTourStep / endTaskTour are stable — deps
    // intentionally omit them to avoid resubscribing every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, targetRect, isLastStep]);

  // Liveness re-measure + route-independent teardown.
  //
  // While targetRect is set, poll the selector every TARGET_RETRY_MS so the
  // spotlight stays glued to a moving target (e.g. a wizard panel that
  // animates) and so we detect when it leaves the DOM without the
  // completeOnDisappear or F6 guards firing (wizard-internal steps that
  // declare no `route`).
  //
  // When target is present  → re-measure (guarded by rect equality to avoid
  //   thrashing) and re-measure cursorRect too.
  // When target is gone:
  //   1. completeOnDisappear → return (that effect owns disappearance semantics).
  //   2. gateSelector currently satisfied → return (gate auto-advance is
  //      imminent; never race it on the happy path).
  //   3. Otherwise count a "gone" streak; once it exceeds ~1000ms clear the
  //      interval and apply the same advance-vs-end decision as completeOnDisappear.
  useEffect(() => {
    if (!step || !targetRect) return undefined;
    const GONE_THRESHOLD = 10; // 10 × TARGET_RETRY_MS (100ms) = 1 000ms
    let goneStreak = 0;
    const interval = window.setInterval(() => {
      const el = document.querySelector(step.selector);
      if (el) {
        goneStreak = 0;
        // Re-measure with equality guard so we only setState when the
        // element has actually moved/resized — avoids thrashing every tick.
        const next = getElementRect(el);
        setTargetRect((prev) => {
          if (
            prev &&
            prev.top === next.top &&
            prev.left === next.left &&
            prev.width === next.width &&
            prev.height === next.height
          )
            return prev;
          return next;
        });
        // Also keep the cursor target fresh.
        if (step.cursorSelector) {
          const cursorEl = document.querySelector(step.cursorSelector);
          setCursorRect(cursorEl ? getElementRect(cursorEl) : null);
        }
        return;
      }
      // Target is gone --------------------------------------------------
      // 1. completeOnDisappear owns this — let its effect handle it.
      if (step.completeOnDisappear) return;
      // 2. Gate is satisfied → gate auto-advance is about to fire (~400ms
      //    delay in the prevGateRef effect); don't race it.
      if (step.gateSelector && document.querySelector(step.gateSelector)) return;
      // 3. Accumulate absence; act once the streak crosses the threshold.
      goneStreak += 1;
      if (goneStreak < GONE_THRESHOLD) return;
      window.clearInterval(interval);
      // Same advance-vs-end logic as completeOnDisappear: check whether the
      // current path matches the next step's declared route.
      const newPath = locationRef.current.pathname;
      const tourKey = activeTourRef.current;
      const nextIdx = stepIndexRef.current + 1;
      const nextStep = tourKey ? TASK_TOURS[tourKey]?.[nextIdx] : undefined;
      if (nextStep?.route && newPath.startsWith(nextStep.route)) {
        if (isLastStep) {
          finishTour();
        } else {
          nextTaskTourStep();
        }
      } else {
        endTaskTour();
      }
    }, TARGET_RETRY_MS);
    return () => window.clearInterval(interval);
    // finishTour / nextTaskTourStep / endTaskTour are stable; omit to avoid
    // resubscribing every render (same pattern as completeOnDisappear above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, targetRect, isLastStep]);

  // F6: end the tour if the merchant navigates away mid-step from a
  // page-bound step (one with a declared `route`). Without this, the
  // overlay persists at stale coordinates over whatever real page they
  // land on. Steps without a `route` are valid on any page — skip them.
  // prevPathRef guards the check so the initial mount and the route-push
  // effect's own navigation don't incorrectly trigger an end.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (!step || !activeTaskTour) return;
    if (!step.route) return;
    if (location.pathname === prevPath) return; // no navigation happened
    if (!location.pathname.startsWith(step.route)) {
      endTaskTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ESC closes the tour, same as clicking the close button.
  useEffect(() => {
    if (!activeTaskTour) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') endTaskTour();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTaskTour, endTaskTour]);

  if (!activeTaskTour || !step || !targetRect) return null;

  const placement: SimplePlacement = (step.placement as SimplePlacement | undefined) ?? 'right';

  // Tour card content — passed as `content` to antd Popover. Positioning
  // is entirely handled by Popover (auto-flip + clamp + arrow); we only
  // author the card's contents here.
  const tourCard = (
    <div className={styles.tooltipInner}>
      <button className={styles.closeBtn} onClick={endTaskTour} aria-label="Close tour" type="button">
        <CloseOutlined style={{ fontSize: 12 }} />
      </button>
      {activeTaskLabel && <div className={styles.taskLabel}>{activeTaskLabel}</div>}
      {step.title && <div className={styles.title}>{step.title}</div>}
      <div className={styles.body}>{step.body}</div>
      {/* Footer visibility rules:
            • Gate satisfied (or last step), non-completeOnDisappear (or last) →
              step counter + Next / Got it.
            • Gate unsatisfied, non-last, non-completeOnDisappear → step counter
              + gate hint; no Next (would be misleading). Merchant can still ESC
              or × to exit, and the hint explains what action is needed.
            • Mid-tour completeOnDisappear (not last) → step counter only;
              merchant must perform the real action to advance.
            • Last step always shows Got it regardless of gate or
              completeOnDisappear — tour can always be finished. */}
      {(gateSatisfied || isLastStep) && (!step.completeOnDisappear || isLastStep) && (
        <div className={styles.footer}>
          {totalSteps > 1 && (
            <span className={styles.stepCounter}>
              {taskTourStepIndex + 1} / {totalSteps}
            </span>
          )}
          <Button size="small" type="primary" onClick={handlePrimaryClick}>
            {isLastStep ? 'Got it' : 'Next'}
          </Button>
        </div>
      )}
      {!gateSatisfied && !isLastStep && !step.completeOnDisappear && (
        <div className={styles.footer}>
          {totalSteps > 1 && (
            <span className={styles.stepCounter}>
              {taskTourStepIndex + 1} / {totalSteps}
            </span>
          )}
          {step.gateHint && <span className={styles.gateHint}>{step.gateHint}</span>}
        </div>
      )}
      {step.completeOnDisappear && !isLastStep && (
        <div className={styles.footer}>
          {totalSteps > 1 && (
            <span className={styles.stepCounter}>
              {taskTourStepIndex + 1} / {totalSteps}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.overlayRoot} aria-hidden="false">
      {/* Popover pins itself to the invisible anchor via rc-align.
          `key` includes the selector so a step change forces a re-mount
          (and a fresh alignment). Within a step, alignment updates via
          antd's built-in window scroll/resize listeners. */}
      <Popover
        key={step.selector}
        visible={true}
        placement={placement}
        content={tourCard}
        overlayClassName={styles.tourPopover}
        // Portal to body so the popover escapes any overflow:hidden
        // ancestors of our overlay root.
        getPopupContainer={() => document.body}
        // Any trigger works here because visible is controlled; onVisibleChange
        // is not wired, so click/hover on the invisible anchor can never
        // dismiss the tour (only ESC, the close button, or completion do).
        trigger="click"
        // Auto-flip + clamp — antd's default is already true; naming it
        // here so the intent is obvious.
        autoAdjustOverflow
      >
        {/* Invisible anchor sized/positioned to match the highlighted
            target plus the highlight padding. pointer-events:none so it
            never intercepts clicks meant for the underlying UI. */}
        <div
          ref={anchorElRef}
          className={styles.anchor}
          style={{
            top: targetRect.top - HIGHLIGHT_PADDING,
            left: targetRect.left - HIGHLIGHT_PADDING,
            width: targetRect.width + HIGHLIGHT_PADDING * 2,
            height: targetRect.height + HIGHLIGHT_PADDING * 2,
          }}
          aria-hidden="true"
        />
      </Popover>

      {/* Animated click cursor — pulses over a target's centre for
          steps that opt in via `showClickCursor`. Uses `cursorRect`
          when the step defines a separate `cursorSelector` (tooltip on
          one thing, cursor on another); otherwise falls back to the
          main `targetRect` so the cursor sits on the tooltip's anchor.
          Rendered outside the Popover so it sits above the underlying
          UI (via .overlayRoot's z-index) but doesn't block clicks
          (pointer-events: none). `targetRect` is guaranteed non-null
          here — the component returns null earlier if it's missing. */}
      {step.showClickCursor && (
        <div
          className={styles.clickCursor}
          style={{
            top: (cursorRect ?? targetRect).top + (cursorRect ?? targetRect).height / 2,
            left: (cursorRect ?? targetRect).left + (cursorRect ?? targetRect).width / 2,
          }}
          aria-hidden="true"
        >
          <span className={styles.clickCursorRipple} />
          <svg width="26" height="30" viewBox="0 0 26 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 3 L4 24 L9.5 19 L13 27 L16.5 25.5 L13 17.5 L20.5 17 Z"
              fill="#fff"
              stroke="#0f172a"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      {celebration && <CelebrationBurst x={celebration.x} y={celebration.y} />}
    </div>
  );
};

// Confetti celebration — portaled to document.body so it escapes both
// .overlayRoot's stacking context AND antd Popover's z-index. Rendered
// as a sibling of the popover portal at body level with a higher z-index,
// so the burst sits ABOVE the tooltip during the 900ms celebration hold.
const CelebrationBurst: React.FC<{
  x: number;
  y: number;
}> = ({ x, y }) => {
  return ReactDOM.createPortal(
    <div className={styles.celebrationRoot} style={{ top: y, left: x, width: 0, height: 0 }}>
      {CONFETTI_PIECES.map((piece, i) => {
        const rad = (piece.angle * Math.PI) / 180;
        const dx = Math.cos(rad) * CONFETTI_DISTANCE;
        const dy = Math.sin(rad) * CONFETTI_DISTANCE - 20; // Slight upward bias.
        const rot = (piece.angle + 180) * 1.4;
        return (
          <span
            key={i}
            className={styles.confetti}
            style={
              {
                background: piece.color,
                animationDelay: `${i * 20}ms`,
                ['--dx' as string]: `${dx}px`,
                ['--dy' as string]: `${dy}px`,
                ['--rot' as string]: `${rot}deg`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>,
    document.body
  );
};

export default TaskTourOverlay;
